import { useState } from "react";
import { useSearchParams } from "react-router-dom";
import { DateValue, getInitialDate } from "~/shared/lib/analytics-utils";
import { CATEGORIES, CategoryKey } from "../components/CategoryChips";
import { DEFAULT_REPORT_TAB, isReportTabId } from "../components/report-tabs";

export function useAnalyticsFilters() {
  const [searchParams, setSearchParams] = useSearchParams();

  // Get parameters from URL or set default values
  const activeTab = searchParams.get("tab") || "charts";
  // An unknown ?report_tab= falls back to the first report.
  const rawReportTab = searchParams.get("report_tab");
  const activeReportTab = isReportTabId(rawReportTab) ? rawReportTab : DEFAULT_REPORT_TAB;
  // Unknown ?category= values fall back to "all" instead of leaking into queries.
  const rawCategory = searchParams.get("category");
  const category: CategoryKey = CATEGORIES.includes(rawCategory as CategoryKey)
    ? (rawCategory as CategoryKey)
    : "all";

  const [startDate, setStartDate] = useState<DateValue>(() =>
    getInitialDate(searchParams.get("start_date") || undefined, "start"),
  );
  const [endDate, setEndDate] = useState<DateValue>(() =>
    getInitialDate(searchParams.get("end_date") || undefined, "end"),
  );

  // Update URL when parameters change
  const updateSearchParams = (updates: Record<string, string>) => {
    const newParams = new URLSearchParams(searchParams);
    Object.entries(updates).forEach(([key, value]) => {
      newParams.set(key, value);
    });
    setSearchParams(newParams);
  };

  // Handlers for tab changes
  const handleTabChange = (newTab: string) => {
    updateSearchParams({ tab: newTab });
  };

  const handleReportTabChange = (newReportTab: string) => {
    updateSearchParams({ report_tab: newReportTab });
  };

  const handleCategoryChange = (newCategory: string) => {
    updateSearchParams({ category: newCategory });
  };

  // Handlers for date changes
  const handleStartDateChange = (date: Date | undefined) => {
    if (date) {
      const newStartDate: DateValue = {
        year: date.getFullYear(),
        month: date.getMonth() + 1,
        day: date.getDate(),
        toString() {
          return `${this.year}-${String(this.month).padStart(2, "0")}-${String(this.day).padStart(2, "0")}`;
        },
      };
      setStartDate(newStartDate);
      updateSearchParams({ start_date: newStartDate.toString() });
    }
  };

  const handleEndDateChange = (date: Date | undefined) => {
    if (date) {
      const newEndDate: DateValue = {
        year: date.getFullYear(),
        month: date.getMonth() + 1,
        day: date.getDate(),
        toString() {
          return `${this.year}-${String(this.month).padStart(2, "0")}-${String(this.day).padStart(2, "0")}`;
        },
      };
      setEndDate(newEndDate);
      updateSearchParams({ end_date: newEndDate.toString() });
    }
  };

  return {
    // State
    activeTab,
    activeReportTab,
    category,
    startDate,
    endDate,

    // Handlers
    handleTabChange,
    handleReportTabChange,
    handleCategoryChange,
    handleStartDateChange,
    handleEndDateChange,
  };
}
